import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IPeople } from "@interfaces/people.interface";
import peopleService from "@services/people.service";
import { getFullNameOrEmail } from "@utils/tags.util";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ALL_PEOPLE_OPTIONS_QUERY_KEY } from "@config/query";

function usePeopleWithFilters(
  searchQuery: string,
  customParams: { initial?: number; limit?: number } = {},
  companyId?: string | null
) {
  return useInfiniteQuery({
    queryKey: [ALL_PEOPLE_OPTIONS_QUERY_KEY, searchQuery, customParams, companyId],
    queryFn: async ({ pageParam }: { pageParam: any }) => {
      const params = new URLSearchParams();
      if (searchQuery) {
        params.append("search", searchQuery);
        params.append("ordering", "first_name");
      } else {
        params.append("limit", String(pageParam.limit));
        params.append("offset", pageParam.offset ? String(pageParam.offset) : "0");
        params.append("ordering", "first_name");
      }
      // Filter by primary_company if companyId is provided
      if (companyId) {
        params.append("primary_company", companyId);
      }
      return await peopleService.searchPeople(params);
    },
    getNextPageParam: (lastPage) => {
      // All results fetched in a single request when searching
      if (searchQuery) return undefined;

      if (customParams.limit !== undefined) {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          const limit = customParams.limit;
          const offset = Number(url.searchParams.get("offset")) || 0;
          return { limit, offset };
        }
        return undefined;
      }
      if (lastPage.next) {
        const url = new URL(lastPage.next);
        const limit = Number(url.searchParams.get("limit")) || 50;
        const offset = Number(url.searchParams.get("offset")) || 0;
        return { limit, offset };
      }
      return undefined;
    },
    select: (data) => {
      return data.pages.flatMap((page) => page.results);
    },
    initialPageParam: { limit: customParams.initial ?? 20, offset: 0 },
  });
}

export const usePersonOptions = (
  customParams: { initial?: number; limit?: number } = {},
  companyId?: string | null
) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [defaultOptions, setDefaultOptions] = useState<IPeople[]>([]);
  const callbackRef = useRef<((arr: IPeople[]) => void) | null>(null);

  const {
    data: allPersons,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = usePeopleWithFilters(searchQuery, customParams, companyId);

  const debouncedSearch = useCallback(
    debounce((inputValue: string) => {
      setSearchQuery(inputValue);
    }, 300),
    []
  );

  const searchedOptions = useMemo(() => {
    if (isLoading) return [];

    return allPersons
      ? allPersons.map((person) => ({
        ...person,
        label: getFullNameOrEmail(person),
        value: person.id,
      }))
      : [];
  }, [allPersons, isLoading]);

  useEffect(() => {
    if (!searchQuery && allPersons) {
      setDefaultOptions(
        allPersons.map((person) => ({
          ...person,
          label: getFullNameOrEmail(person),
          value: person.id,
        }))
      );
    }
  }, [searchQuery, allPersons]);

  useEffect(() => {
    if (isLoading) return;
    if (callbackRef.current) {
      callbackRef.current(searchedOptions);
      callbackRef.current = null;
    }
  }, [searchedOptions, isLoading]);

  const loadOptions = (_: string, callback: (arr: IPeople[]) => void) => {
    callbackRef.current = callback;
  };

  return {
    loadOptions,
    debouncedSearch,
    searchQuery,
    setSearchQuery,
    defaultOptions,
    searchedOptions,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
  };
};
